import { motion } from "framer-motion";
import { MapPin, Phone, Clock } from "lucide-react";

const doctors = [
  { name: "Dr. Arun Paul", degree: "MDS", role: "Root Canal Specialist" },
  { name: "Dr. Leona Arun", degree: "BDS", role: "Chief Dental Surgeon" },
];

const branches = [
  {
    key: "apex",
    n: "01",
    name: "Apex Dental Care",
    tag: "Edamuttam · Est. 2026",
    address: [
      "Ground Floor, Kodanchery Arcade,",
      "Edamuttam Kattoor Road, Edamuttam",
      "(Near Shanti Medicals, Opposite Aswini Lab)",
    ],
    phone: "+91 99617 17181",
    tel: "+919961717181",
    hours: "Mon – Sat · 9:30 AM – 6:00 PM",
    sunday: "Sunday · Closed",
    maps: "https://maps.app.goo.gl/abxZS66jBguHm6Ru6",
    accent: true,
  },
  {
    key: "alpha",
    n: "02",
    name: "Alpha Dental Care",
    tag: "Kattoor · Est. 2006",
    address: [
      "1st Floor, Fathima Complex,",
      "Opp. Morning & Evening Co-Op Branch,",
      "Kattoor",
    ],
    phone: "+91 94006 77956",
    tel: "+919400677956",
    hours: "Mon – Sat · 2:00 PM – 7:00 PM",
    sunday: "Sunday · Closed",
    maps: "https://www.google.com/maps/search/?api=1&query=Alpha+Dental+Care+Kattoor+Fathima+Complex",
  },
  {
    key: "neethi",
    n: "03",
    name: "Neethi Dental Care",
    tag: "Edathiruthy · Est. 2017",
    address: [
      "Ground Floor,",
      "Edathiruthy Co-operative Bank Building,",
      "Edathiruthy",
    ],
    phone: "+91 94001 21956",
    tel: "+919400121956",
    hours: "Mon – Sat · 9:30 AM – 1:30 PM",
    sunday: "Sunday · Closed",
    maps: "https://www.google.com/maps/search/?api=1&query=Neethi+Dental+Care+Edathiruthy+Co-operative+Bank",
  },
];

export default function Branches() {
  return (
    <section
      id="branches"
      data-testid="branches-section"
      className="relative py-28 md:py-40 bg-porcelain border-b border-bone"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
        {/* Section header + doctors */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-20 md:mb-28">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-ink" />
              <span className="eyebrow">Our Practices</span>
              <span className="font-malayalam text-[13px] text-ink/60 leading-none">നമ്മുടെ ശാഖകൾ</span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
              className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight"
              data-testid="branches-heading"
              style={{ color: "#4A3520" }}
            >
              Three practices.<br />
              <span className="italic" style={{ color: "#B8944E" }}>One promise.</span>
            </motion.h2>
          </div>

          <div className="col-span-12 md:col-span-7 md:pt-4">
            <p className="text-ink/70 text-base md:text-lg leading-relaxed max-w-[46ch] font-light">
              Under the care of Dr. Arun Paul and Dr. Leona Arun — two decades of specialist
              dentistry, practised at three quiet clinics across Thrissur district.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {doctors.map((d) => (
                <div key={d.name} className="border-t border-ink/30 pt-6" data-testid={`doctor-${d.name.split(" ").join("-").toLowerCase()}`}>
                  <div className="eyebrow mb-2">{d.role}</div>
                  <div className="font-serif text-xl md:text-2xl leading-tight text-ink">
                    {d.name} <span className="text-taupe">·</span> <span className="italic text-taupe">{d.degree}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Branches grid */}
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {branches.map((b, i) => (
            <motion.article
              key={b.key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: [0.76, 0, 0.24, 1] }}
              className={`col-span-12 md:col-span-6 lg:col-span-4 flex flex-col ${b.accent ? "bg-ink text-porcelain p-10 md:p-12" : "bg-white p-10 md:p-12 border border-bone"}`}
              data-testid={`branch-card-${b.key}`}
            >
              <div className="flex items-baseline justify-between mb-8">
                <span className={`eyebrow ${b.accent ? "!text-porcelain/60" : ""}`}>{b.n}</span>
                <span className={`eyebrow ${b.accent ? "!text-porcelain/60" : ""}`}>{b.tag}</span>
              </div>

              <h3 className={`font-serif font-light text-3xl md:text-4xl leading-tight tracking-tight ${b.accent ? "text-porcelain" : "text-ink"}`}>
                {b.name}
              </h3>

              <div className={`mt-8 flex-1 space-y-6 ${b.accent ? "" : ""}`}>
                <div className="flex items-start gap-4">
                  <MapPin size={16} className={`mt-1 shrink-0 ${b.accent ? "text-taupe" : "text-taupe"}`} />
                  <a
                    href={b.maps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`font-serif text-lg leading-snug ${b.accent ? "text-porcelain/90 hover:text-taupe" : "text-ink hover:text-taupe"} transition-colors duration-500`}
                    data-testid={`branch-maps-${b.key}`}
                  >
                    {b.address.map((line, idx) => (
                      <span key={idx} className="block">
                        {line}
                      </span>
                    ))}
                  </a>
                </div>

                <div className="flex items-start gap-4">
                  <Phone size={16} className="mt-1 text-taupe shrink-0" />
                  <a
                    href={`tel:${b.tel}`}
                    className={`text-base ${b.accent ? "text-porcelain" : "text-ink"} link-underline`}
                    style={{ fontFamily: '"Outfit", sans-serif', fontWeight: 400, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' }}
                    data-testid={`branch-phone-${b.key}`}
                  >
                    {b.phone}
                  </a>
                </div>

                <div className="flex items-start gap-4">
                  <Clock size={16} className="mt-1 text-taupe shrink-0" />
                  <div className={`text-base ${b.accent ? "text-porcelain/80" : "text-ink/80"} font-light leading-snug`}>
                    {b.hours}
                    <div className={`text-sm mt-1 ${b.accent ? "text-porcelain/50" : "text-ink/50"}`}>{b.sunday}</div>
                  </div>
                </div>
              </div>

              <a
                href={`tel:${b.tel}`}
                data-testid={`branch-cta-${b.key}`}
                className={`mt-10 inline-flex items-center gap-3 text-[12px] tracking-[0.24em] uppercase pt-6 border-t ${b.accent ? "border-porcelain/20 text-porcelain hover:text-taupe" : "border-bone text-ink hover:text-taupe"} transition-colors duration-500 group`}
              >
                <span>Call this branch</span>
                <span aria-hidden className="transition-transform duration-500 group-hover:translate-x-1.5">→</span>
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
